import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, delay, map, mergeMap, of, switchMap, withLatestFrom } from "rxjs";
import { UserActions } from "./user.actions";
import { Company, User, UserClient } from "src/api-service";
import { DataActions } from "../application/data/data.actions";
import { Store } from "@ngrx/store";
import { selectSelectedCompany } from "./user.selectors";
import { environment } from "@env/environment";
import { normalizeError } from "../app-store-error.types";

// List store actions for reload on company change
import { QuotesActions } from "../pages/quote/quotes/quotes.actions";
import { CustomersActions } from "../pages/customer/customers/customers.actions";
import { ContractsActions } from "../pages/contract/contracts/contracts.actions";
import { CatalogsActions } from "../pages/catalog/catalogs/catalogs.actions";
import { SalesOrdersActions } from "../pages/sales-order/sales-orders/sales-orders.actions";
import { VendorsActions } from "../pages/vendor/vendors/vendors.actions";
import { NotificationActions } from "../application/notification/notification.actions";

@Injectable()
export class UserEffects {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly userClient = inject(UserClient);

    // ************ INITIALIZE USER *********** //
    initializeUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.initializeUser),
            delay(environment.signInLoadingTimeMs),
            switchMap(({ account }) =>
                this.userClient.get().pipe(
                    map(apiUser => {
                        const companies: Company[] = apiUser.companies;

                        let selectedCompanyId = localStorage.getItem("selectedCompanyId");
                        let selectedCompany = companies?.find(c => c.id === selectedCompanyId) ?? companies[0];

                        if (selectedCompany) {
                            selectedCompanyId = selectedCompany.id;
                            localStorage.setItem("selectedCompanyId", selectedCompanyId);
                        }

                        const user: User = {
                            name: account.name ?? "",
                            email: apiUser?.email ? apiUser?.email : account.username,
                            siteLanguageCode: apiUser?.siteLanguageCode ? apiUser?.siteLanguageCode : "sv",
                            phoneNumber: apiUser?.phoneNumber ? apiUser?.phoneNumber : undefined,
                            profilePhotoUrl: apiUser?.profilePhotoUrl ?? undefined,
                            companies: companies as Company[],
                            userRoles: apiUser?.userRoles,
                            defaultSendQuoteToSelf: apiUser?.defaultSendQuoteToSelf,
                        };
                        return UserActions.initializeUserSuccess({ user, selectedCompany: selectedCompany as Company });
                    }),
                    catchError(error => of(UserActions.initializeUserFailure({ error: normalizeError(error), toastKey: "USER.TOAST.INITIALIZE_ERROR" }))),
                ),
            ),
        ),
    );

    // ************ UPDATE USER LANGUAGE *********** //
    updateUserLanguage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.updateUserLanguage),
            mergeMap(({ language }) =>
                this.userClient.get().pipe(
                    switchMap(user => {
                        const updatedUser = { ...user, siteLanguageCode: language };
                        return this.userClient.update(updatedUser).pipe(
                            map(() => UserActions.updateUserLanguageSuccess({ language })),
                            catchError(error => of(UserActions.updateUserFailure({ error: normalizeError(error), toastKey: "USER.TOAST.UPDATE_ERROR" }))),
                        );
                    }),
                    catchError(error => of(UserActions.updateUserFailure({ error: normalizeError(error), toastKey: "USER.TOAST.UPDATE_ERROR" }))),
                ),
            ),
        ),
    );

    updateUserLanguageSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.updateUserLanguageSuccess),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            map(([, selectedCompany]) => DataActions.initializeData({ companyId: selectedCompany?.id ?? "" })),
        ),
    );

    // CHANGE COMPANY
    changeCompany$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.changeCompany),
            switchMap(({ company }) => {
                localStorage.setItem("selectedCompanyId", company.id);
                return of(UserActions.changeCompanySuccess({ company }));
            }),
        ),
    );

    updatePhoneNumber$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.updatePhoneNumber),
            switchMap(({ phoneNumber }) =>
                this.userClient.get().pipe(
                    switchMap(user => {
                        const updatedUser = { ...user, phoneNumber: phoneNumber.trim() };
                        return this.userClient.update(updatedUser).pipe(
                            map(savedUser => UserActions.updatePhoneNumberSuccess({
                                phoneNumber: savedUser.phoneNumber,
                                toastKey: "USER.TOAST.PHONE_NUMBER_UPDATED"
                            })),
                            catchError(error => of(UserActions.updatePhoneNumberFailure({
                                error,
                                toastKey: "USER.TOAST.PHONE_NUMBER_ERROR"
                            }))),
                        );
                    }),
                    catchError(error => of(UserActions.updatePhoneNumberFailure({
                        error,
                        toastKey: "USER.TOAST.PHONE_NUMBER_ERROR"
                    }))),
                ),
            ),
        ),
    );

    // Reload all stores when company changes (centralized - add new reloads here)
    reloadOnCompanyChange$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.changeCompanySuccess),
            mergeMap(({ company }) => [
                QuotesActions.load({}),
                CustomersActions.load({}),
                ContractsActions.load({}),
                CatalogsActions.load(),
                SalesOrdersActions.load({}),
                VendorsActions.load({}),
                NotificationActions.loadUserNotifications({
                    countryCode: company.countryCode,
                    companyId: company.id,
                }),
            ]),
        ),
    );

    updateDefaultSendQuoteToSelf$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UserActions.updateDefaultSendQuoteToSelf),
            mergeMap(({ defaultSendQuoteToSelf }) =>
                this.userClient.get().pipe(
                    switchMap(user => {
                        const updatedUser = { ...user, defaultSendQuoteToSelf };
                        return this.userClient.update(updatedUser).pipe(
                            map(() => UserActions.updateDefaultSendQuoteToSelfSuccess({ defaultSendQuoteToSelf })),
                            catchError(error => of(UserActions.updateDefaultSendQuoteToSelfFailure({ error: normalizeError(error), toastKey: "USER.TOAST.UPDATE_SEND_QUOTE_TO_SELF_ERROR" }))),
                        );
                    }),
                    catchError(error => of(UserActions.updateDefaultSendQuoteToSelfFailure({ error: normalizeError(error), toastKey: "USER.TOAST.UPDATE_SEND_QUOTE_TO_SELF_ERROR" }))),
                ),
            ),
        ),
    );
}
